/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { createFeature, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Cluster, Message, MessageType } from 'CalltakingCoreApi';
import {
    fetchDtmfMessagesSuccess,
    fetchPredefinedSmsMessagesSuccess,
    fetchPredefinedTddMessagesSuccess,
    fetchRttMessagesSuccess,
    fetchSmsMessagesSuccess,
    fetchTddMessagesSuccess,
    fetchTextCallHistorySuccess,
    fetchTranslationLanguagesFail,
    fetchTranslationLanguagesSuccess,
    fetchDialogStateSuccess,
    fetchDialogStateFail,
    pendingMessageAdded,
    messageEffectsInitialized,
    newDtmfMessage,
    newRttMessage,
    newSmsMessage,
    newTddMessage,
    purgeMessages,
    updateDtmfMessage,
    updateMessageSubscriptions,
    updateMultipleMessageSubscriptions,
    pendingMessageTranslated,
    pendingMessageRemove,
    updateRttMessage,
    updateSmsMessage,
    updateTddMessage,
    updateTranslationSettings,
    MessageErrorType,
    messageErrorMarkTranslationError,
    messageErrorRemoveTranslationError,
    messageErrorRetry,
    messageErrorRemove,
    manualLanguageOverrideSet,
    manualLanguageOverrideCleared
} from './message.actions';
import { SortFunctions } from '../../call/util/sort-functions';
import { PredefinedMessages } from '../model/pre-defined-messages';
import { HistoricalTextMessage } from '../model/historical-text-message';
import { DialogState } from '../model/dialog.model';
import { initSubscriptionState } from '../../configuration/+state/configuration.actions';
import { MultipleSubscriptionsStatus, Topic } from '../../user/model/multiple-subscriptions';
import { sendSmsMessageFail, sendTddMessageFail } from '../../call/+state/call.actions';
import { TranslationLanguage, TranslationSettings, DisplayMessages, TranslationIndicator,
    MessageErrorParams, ManualLanguageOverride } from '../model/translation.model';
import { createManualLanguageOverride } from '../util/translation.util';

export type MessageErrorBucket = { ids: string[]; params: Record<string, MessageErrorParams> };
export type MessageErrorsByType = Record<MessageErrorType, MessageErrorBucket>;

export interface MessageState {
    subscriptions: { [topic: string]: { [receipt: string]: boolean } };
    multipleSubscriptions: MultipleSubscriptionsStatus;
    initialized: boolean;
    sms: SMSMessageState;
    tdd: TDDMessageState;
    rtt: RTTMessageState;
    dtmf: DTMFMessageState;
    smsCallMap: { [callId: string]: { [messageId: string]: Message } };
    tddCallMap: { [callId: string]: { [messageId: string]: Message } };
    rttCallMap: { [callId: string]: { [messageId: string]: Message } };
    dtmfCallMap: { [callId: string]: { [messageId: string]: Message } };
    historicalTextMap: { [ref: string]: { [callId: string]: Message[] } };
    default: DefaultMessageState;
    predefinedSms: PredefinedMessages[];
    predefinedTdd: PredefinedMessages[];
    smsHistorical: SMSHistoricalMessageState;
    translationLanguages: TranslationLanguage[];
    pendingMessages: Message[];
    errorsByType: MessageErrorsByType;
    translationSettings: TranslationSettings;
    dialogState: DialogStateEntityState;
    manualLanguageOverride: { [callId: string]: ManualLanguageOverride | null };
}

export interface SMSMessageState extends EntityState<Message> {}

export interface TDDMessageState extends EntityState<Message> {}

export interface RTTMessageState extends EntityState<Message> {}

export interface DTMFMessageState extends EntityState<Message> {}

export interface DefaultMessageState extends EntityState<Message> {}

export interface SMSHistoricalMessageState extends EntityState<HistoricalTextMessage> {}

export interface CallDialogState extends DialogState {
    callId: string;
}

export interface DialogStateEntityState extends EntityState<CallDialogState> {}

export const dialogStateAdapter: EntityAdapter<CallDialogState> = createEntityAdapter<CallDialogState>({
    selectId: (dialogState) => dialogState.callId
});

export const smsAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const tddAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const rttAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const dtmfAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const defaultAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const smsHistoricalAdapter: EntityAdapter<HistoricalTextMessage> = createEntityAdapter<HistoricalTextMessage>({
    selectId: (message: HistoricalTextMessage) => message.uuid
});

enum MessageKey {
    SMS = 'sms',
    TDD = 'tdd',
    RTT = 'rtt',
    DTMF = 'dtmf'
}

enum CallMapKey {
    SMS = 'smsCallMap',
    TDD = 'tddCallMap',
    RTT = 'rttCallMap',
    DTMF = 'dtmfCallMap'
}

const initialSmsState: SMSMessageState = smsAdapter.getInitialState({});

const initialTddState: TDDMessageState = tddAdapter.getInitialState({});

const initialRttState: TDDMessageState = rttAdapter.getInitialState({});

const initialDtmfState: DTMFMessageState = dtmfAdapter.getInitialState({});

const initialDefaultState: DefaultMessageState = defaultAdapter.getInitialState({});

const initialSmsHistoricalState: SMSHistoricalMessageState = smsHistoricalAdapter.getInitialState({});

const initialDialogState: DialogStateEntityState = dialogStateAdapter.getInitialState({});

const initialState: MessageState = {
    subscriptions: {},
    multipleSubscriptions: {},
    initialized: false,
    sms: initialSmsState,
    tdd: initialTddState,
    rtt: initialRttState,
    dtmf: initialDtmfState,
    smsCallMap: {},
    tddCallMap: {},
    rttCallMap: {},
    dtmfCallMap: {},
    historicalTextMap: {},
    default: initialDefaultState,
    predefinedSms: [],
    predefinedTdd: [],
    smsHistorical: initialSmsHistoricalState,
    translationLanguages: [],
    pendingMessages: [],
    errorsByType: {
        [MessageErrorType.TRANSLATION]: { ids: [], params: {} },
        [MessageErrorType.SEND]: { ids: [], params: {} },
        [MessageErrorType.DELIVERY]: { ids: [], params: {} }
    },
    translationSettings: { displayMessages: DisplayMessages.ALL, translationIndicator: TranslationIndicator.SHOW_ALL },
    dialogState: initialDialogState,
    manualLanguageOverride: {}
};

function addMessageError(state: MessageState, messageId: string, errorType: MessageErrorType, params: MessageErrorParams): MessageState {
    const bucket = state.errorsByType[errorType];
    return {
        ...state,
        errorsByType: {
            ...state.errorsByType,
            [errorType]: {
                ids: [...bucket.ids, messageId],
                params: { ...bucket.params, [messageId]: params }
            }
        }
    };
}

function removeMessageError(
    state: MessageState,
    messageId: string,
    errorType: MessageErrorType
): MessageState {
    const bucket = state.errorsByType[errorType];
    const { [messageId]: _removed, ...remainingParams } = bucket.params;

    return {
        ...state,
        errorsByType: {
            ...state.errorsByType,
            [errorType]: {
                ids: bucket.ids.filter(id => id !== messageId),
                params: remainingParams
            }
        }
    };
}

function buildErrorParamsFromMessage(message: Message, callId?: string, clusterName?: string): MessageErrorParams {
    return {
        messageType: message.type as MessageType,
        message: message.message || '',
        callId: callId || message.callId,
        clusterName: clusterName || (message as { clusterName?: string }).clusterName || '',
        predefined: message.predefined || false,
        language: message.predefinedLanguage || '',
        userName: message.sender,
        sourceText: message.sourceText,
        sourceLanguage: message.sourceLanguage,
        targetText: message.targetText,
        targetLanguage: message.targetLanguage,
        messageLanguage: message.messageLanguage
    };
}

function replacePendingMessage(state: MessageState, uuid: string, updater: (pending: Message) => Message): MessageState {
    const idx = state.pendingMessages.findIndex(m => m.uuid === uuid);
    if (idx === -1) {
        return state;
    }
    const pendingMessages = [
        ...state.pendingMessages.slice(0, idx),
        updater(state.pendingMessages[idx]),
        ...state.pendingMessages.slice(idx + 1)
    ];
    return { ...state, pendingMessages };
}

const TRANSLATION_FIELD_NAMES = ['sourceText', 'sourceLanguage', 'targetText', 'targetLanguage', 'messageLanguage'] as const;
type TranslationFieldName = typeof TRANSLATION_FIELD_NAMES[number];

function mergeTranslationFields(incoming: Message, fallback: Message | undefined): Message {
    if (!fallback) {
        return incoming;
    }
    const merged: Message = { ...incoming };
    for (const field of TRANSLATION_FIELD_NAMES) {
        const incomingValue = (incoming as Record<TranslationFieldName, unknown>)[field];
        const fallbackValue = (fallback as Record<TranslationFieldName, unknown>)[field];
        if ((incomingValue === undefined || incomingValue === null) && fallbackValue !== undefined && fallbackValue !== null) {
            (merged as Record<TranslationFieldName, unknown>)[field] = fallbackValue;
        }
    }
    return merged;
}

function updateMessageInState(
    state: MessageState,
    message: Message,
    adapter: EntityAdapter<Message>,
    stateKey: MessageKey,
    callMapKey: CallMapKey
): MessageState {
    const pending = state.pendingMessages.find((p) => p.uuid === message.uuid);
    const existing = (state[stateKey] as EntityState<Message>).entities[message.uuid];
    const fallbackSource = pending ?? existing ?? undefined;
    const mergedMessage = mergeTranslationFields(message, fallbackSource);
    return {
        ...state,
        [stateKey]: adapter.setOne(mergedMessage, state[stateKey]),
        [callMapKey]: {
            ...state[callMapKey],
            [mergedMessage.callId]: {
                ...state[callMapKey][mergedMessage.callId],
                [mergedMessage.uuid]: mergedMessage
            }
        },
        pendingMessages: state.pendingMessages.filter((p) => p.uuid !== mergedMessage.uuid)
    };
}

function handleMessage(
    state: MessageState,
    message: Message,
    adapter: EntityAdapter<Message>,
    stateKey: MessageKey,
    callMapKey: CallMapKey,
    clusterName: string
): MessageState {
    if (message.status === 'FAILED') {
        const pendingMessage = state.pendingMessages.find(p => p.uuid === message.uuid);

        if (pendingMessage) {
            const params = buildErrorParamsFromMessage(message, undefined, clusterName);
            const baseUpdate = updateMessageInState(state, message, adapter, stateKey, callMapKey);
            return addMessageError(baseUpdate, message.uuid, MessageErrorType.DELIVERY, params);
        }

        return updateMessageInState(state, message, adapter, stateKey, callMapKey);
    }

    let newState = updateMessageInState(state, message, adapter, stateKey, callMapKey);
    newState = removeMessageError(newState, message.uuid, MessageErrorType.DELIVERY);
    newState = removeMessageError(newState, message.uuid, MessageErrorType.SEND);
    return newState;
}

export const messageFeature = createFeature({
    name: 'messages',
    reducer: createReducer(
        initialState,
        on(messageEffectsInitialized, (state): MessageState => {
            return { ...state, initialized: true };
        }),
        on(updateMessageSubscriptions, (state, { subscriptions }): MessageState => {
            return { ...state, subscriptions: { ...state.subscriptions, ...subscriptions } };
        }),
        on(initSubscriptionState, (state, { config }) => {
            const multipleSubscriptions: MultipleSubscriptionsStatus = {};
            config.clusters.forEach((c: Cluster) => {
                multipleSubscriptions[c.name] = {
                    [Topic.MESSAGES_DTMF_NEW]: false,
                    [Topic.MESSAGES_DTMF_EDIT]: false,
                    [Topic.MESSAGES_DTMF_DELETE]: false,
                    [Topic.MESSAGES_RTT_NEW]: false,
                    [Topic.MESSAGES_RTT_EDIT]: false,
                    [Topic.MESSAGES_RTT_DELETE]: false,
                    [Topic.MESSAGES_SMS_NEW]: false,
                    [Topic.MESSAGES_SMS_EDIT]: false,
                    [Topic.MESSAGES_SMS_DELETE]: false,
                    [Topic.MESSAGES_TDD_NEW]: false,
                    [Topic.MESSAGES_TDD_EDIT]: false,
                    [Topic.MESSAGES_TDD_DELETE]: false,
                    [Topic.MESSAGES_SYSTEM_ERROR_NEW]: false,
                    [Topic.MESSAGES_SYSTEM_ERROR_EDIT]: false,
                    [Topic.MESSAGES_SYSTEM_ERROR_DELETE]: false
                };
            });
            return {...state, multipleSubscriptions};
        }),
        on(updateMultipleMessageSubscriptions, (state, { topic, clusterName, status }) => {
            const currentSubscriptions = state.multipleSubscriptions;
            const subscriptions = {
                ...currentSubscriptions,
                [clusterName]: {
                    ...currentSubscriptions[clusterName],
                    [topic]: status
                }
            };
            return { ...state,  multipleSubscriptions: subscriptions };
        }),
        on(fetchSmsMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                sms: smsAdapter.upsertMany(messages, state.sms),
                smsCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.smsCallMap }
                )
            };
        }),
        on(fetchTddMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                tdd: tddAdapter.upsertMany(messages, state.tdd),
                tddCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.tddCallMap }
                )
            };
        }),
        on(fetchRttMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                rtt: rttAdapter.upsertMany(messages, state.rtt),
                rttCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.rttCallMap }
                )
            };
        }),
        on(fetchDtmfMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                dtmf: dtmfAdapter.upsertMany(messages, state.dtmf),
                dtmfCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.dtmfCallMap }
                )
            };
        }),
        on(newSmsMessage, (state, { message, clusterName }) => handleMessage(state, message, smsAdapter, MessageKey.SMS, CallMapKey.SMS, clusterName)),
        on(updateSmsMessage, (state, { message, clusterName }) => handleMessage(state, message, smsAdapter, MessageKey.SMS, CallMapKey.SMS, clusterName)),
        on(newTddMessage, (state, { message, clusterName }) => handleMessage(state, message, tddAdapter, MessageKey.TDD, CallMapKey.TDD, clusterName)),
        on(updateTddMessage, (state, { message, clusterName }) => handleMessage(state, message, tddAdapter, MessageKey.TDD, CallMapKey.TDD, clusterName)),
        on(newRttMessage, (state, { message, clusterName }) => handleMessage(state, message, rttAdapter, MessageKey.RTT, CallMapKey.RTT, clusterName)),
        on(updateRttMessage, (state, { message, clusterName }) => handleMessage(state, message, rttAdapter, MessageKey.RTT, CallMapKey.RTT, clusterName)),
        on(newDtmfMessage, (state, { message, clusterName }) => handleMessage(state, message, dtmfAdapter, MessageKey.DTMF, CallMapKey.DTMF, clusterName)),
        on(updateDtmfMessage, (state, { message, clusterName }) => handleMessage(state, message, dtmfAdapter, MessageKey.DTMF, CallMapKey.DTMF, clusterName)),
        on(manualLanguageOverrideSet, (state, { callId, languageCode, timestamp }): MessageState => {
            const override = createManualLanguageOverride(languageCode, timestamp);
            return {
                ...state,
                manualLanguageOverride: { ...state.manualLanguageOverride, [callId]: override }
            };
        }),
        on(manualLanguageOverrideCleared, (state, { callId }): MessageState => {
            return {
                ...state,
                manualLanguageOverride: { ...state.manualLanguageOverride, [callId]: null }
            };
        }),
        on(fetchTextCallHistorySuccess, (state, { referenceKey, messages }): MessageState => {
            return {
                ...state,
                historicalTextMap: messages.reduce<{ [ref: string]: { [callId: string]: Message[] } }>(
                    (map, message) => ({
                        ...map,
                        [referenceKey]: {
                            ...map[referenceKey],
                            [message.callId]:
                                map[referenceKey] && map[referenceKey][message.callId] ? [...map[referenceKey][message.callId]].concat(message) : [message]
                        }
                    }),
                    {}
                )
            };
        }),
        on(purgeMessages, (state, { uuids }) => {
            let smsCallMap = { ...state.smsCallMap };
            uuids.forEach((uuid) => delete smsCallMap[uuid]);
            let tddCallMap = { ...state.tddCallMap };
            uuids.forEach((uuid) => delete tddCallMap[uuid]);
            let rttCallMap = { ...state.rttCallMap };
            uuids.forEach((uuid) => delete rttCallMap[uuid]);
            let dtmfCallMap = { ...state.dtmfCallMap };
            uuids.forEach((uuid) => delete dtmfCallMap[uuid]);
            let manualLanguageOverride = { ...state.manualLanguageOverride };
            uuids.forEach((uuid) => delete manualLanguageOverride[uuid]);
            return {
                ...state,
                sms: smsAdapter.removeMany((message) => uuids.includes(message.callId), state.sms),
                tdd: tddAdapter.removeMany((message) => uuids.includes(message.callId), state.tdd),
                rtt: rttAdapter.removeMany((message) => uuids.includes(message.callId), state.rtt),
                dtmf: dtmfAdapter.removeMany((message) => uuids.includes(message.callId), state.dtmf),
                default: defaultAdapter.removeMany((message) => uuids.includes(message.callId), state.default),
                dialogState: dialogStateAdapter.removeMany(uuids, state.dialogState),
                manualLanguageOverride,
                smsCallMap,
                tddCallMap,
                rttCallMap,
                dtmfCallMap,
                pendingMessages: state.pendingMessages.filter((m) => !uuids.includes(m.callId))
            };
        }),
        on(pendingMessageAdded, (state, { message }): MessageState => {
            const stateWithoutError = removeMessageError(state, message.uuid, MessageErrorType.TRANSLATION);
            const existingIndex = stateWithoutError.pendingMessages.findIndex(m => m.uuid === message.uuid);
            if (existingIndex !== -1) {
                return replacePendingMessage(stateWithoutError, message.uuid, (existing) => ({ ...message, createdTimestamp: existing.createdTimestamp }));
            }
            return { ...stateWithoutError, pendingMessages: [...stateWithoutError.pendingMessages, message] };
        }),
        on(pendingMessageTranslated, (state, { messageId, message, sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage }): MessageState => {
            return replacePendingMessage(state, messageId, (pending) => ({
                ...pending,
                ...(message !== undefined ? { message } : {}),
                sourceText,
                sourceLanguage,
                targetText,
                targetLanguage,
                messageLanguage
            } as Message));
        }),
        on(messageErrorMarkTranslationError, (state, { messageId, messageType, message, callId, clusterName, predefined, language, userName }): MessageState => {
            return addMessageError(state, messageId, MessageErrorType.TRANSLATION, { messageType, message, callId, clusterName, predefined, language, userName });
        }),
        on(messageErrorRemoveTranslationError, (state, { messageId }): MessageState => {
            return removeMessageError(state, messageId, MessageErrorType.TRANSLATION);
        }),
        on(messageErrorRetry, messageErrorRemove, (state, action): MessageState => {
            return removeMessageError(state, action.messageId, action.errorType);
        }),
        on(pendingMessageRemove, (state, { messageId }): MessageState => {
            let newState = { ...state, pendingMessages: state.pendingMessages.filter(m => m.uuid !== messageId) };
            newState = removeMessageError(newState, messageId, MessageErrorType.TRANSLATION);
            newState = removeMessageError(newState, messageId, MessageErrorType.SEND);
            newState = removeMessageError(newState, messageId, MessageErrorType.DELIVERY);
            return newState;
        }),
        on(sendSmsMessageFail, sendTddMessageFail, (state, { clientMessageId, callId, clusterName }): MessageState => {
            if (!clientMessageId) {
                return state;
            }

            const pendingMessage = state.pendingMessages.find(p => p.uuid === clientMessageId);
            if (!pendingMessage) {
                return state;
            }

            const params = buildErrorParamsFromMessage(pendingMessage, callId, clusterName ?? (pendingMessage as { clusterName?: string }).clusterName);

            return addMessageError(state, clientMessageId, MessageErrorType.SEND, params);
        }),
        on(fetchPredefinedSmsMessagesSuccess, (state, { messages }): MessageState => {
            return { ...state, predefinedSms: messages };
        }),
        on(fetchPredefinedTddMessagesSuccess, (state, { messages }): MessageState => {
            return { ...state, predefinedTdd: messages };
        }),
        on(fetchTranslationLanguagesSuccess, (state, { languages }): MessageState => {
            return { ...state, translationLanguages: languages };
        }),
        on(fetchTranslationLanguagesFail, (state): MessageState => {
            console.warn('reducer fetchTranslationLanguagesFail — translationLanguages reset to []');
            return { ...state, translationLanguages: [] };
        }),
        on(updateTranslationSettings, (state, { settings }): MessageState => {
            return { ...state, translationSettings: settings };
        }),

        on(fetchDialogStateSuccess, (state, { callId, dialogState }): MessageState => {
            return { ...state, dialogState: dialogStateAdapter.upsertOne({ ...dialogState, callId }, state.dialogState) };
        }),
        on(fetchDialogStateFail, (state): MessageState => {
            return state;
        })
    )
});
